import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Star } from "lucide-react";
import { BRAND, TESTIMONIALS, findEvent, EVENTS, FEATURED_QUOTES } from "../data/content";
import PolaroidCard from "../components/scrapbook/PolaroidCard";
import MaskingTape from "../components/scrapbook/MaskingTape";
import { PrimaryButton } from "../components/scrapbook/PrimaryButton";
import NotFound from "./NotFound";

const EventReviews = () => {
    const { eventId } = useParams();
    const event = findEvent(eventId);

    useEffect(() => {
        if (event) {
            document.title = `${event.name} Reviews — PopInReviews.ca`;
            const desc = document.querySelector('meta[name="description"]');
            if (desc) {
                desc.setAttribute(
                    "content",
                    `Real reviews from attendees at ${event.name}. See who actually showed up, who you missed, and why PopIn fixed blind networking.`,
                );
            }
        }
        return () => {
            document.title = "PopInReviews.ca — Stop Blind Networking";
        };
    }, [event]);

    if (!event) return <NotFound />;

    // Rotate testimonials per-event so each page feels distinct
    const rotated = [...TESTIMONIALS, ...TESTIMONIALS].slice(
        EVENTS.findIndex((e) => e.id === event.id),
        EVENTS.findIndex((e) => e.id === event.id) + 6,
    );

    return (
        <main
            data-testid={`event-page-${event.id}`}
            className="pt-28 sm:pt-32 pb-24"
        >
            {/* SEO H1 + breadcrumb */}
            <section className="relative px-4 sm:px-6 py-16 sm:py-20 paper-noise">
                <div className="max-w-5xl mx-auto">
                    <Link
                        to="/"
                        data-testid="event-back-link"
                        className="inline-flex items-center gap-2 font-handwriting text-xl text-ink-light hover:text-marker-red transition-colors mb-6"
                    >
                        <ArrowLeft size={18} /> back to all reviews
                    </Link>

                    <div className="relative inline-block">
                        <span
                            className="stamp text-sm"
                            style={{ color: event.color }}
                        >
                            verified attendees
                        </span>
                    </div>

                    <h1
                        data-testid="event-headline"
                        className="font-marker text-5xl sm:text-7xl md:text-[5.5rem] leading-[0.95] tracking-tight text-ink mt-4"
                    >
                        Reviews from{" "}
                        <span style={{ color: event.color }}>
                            {event.shortName}
                        </span>
                    </h1>

                    <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-handwriting text-xl text-ink-light">
                        <span className="inline-flex items-center gap-1.5">
                            <MapPin size={16} /> {event.city}
                        </span>
                        <span className="text-ink/30">·</span>
                        <span className="inline-flex items-center gap-1.5">
                            <Calendar size={16} /> Hosted by {event.host}
                        </span>
                        <span className="text-ink/30">·</span>
                        <span className="inline-flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    size={14}
                                    className="text-marker-red fill-marker-red"
                                />
                            ))}
                        </span>
                    </div>

                    <p className="font-editorial italic text-xl sm:text-2xl text-ink-light max-w-2xl mt-8 leading-snug">
                        {event.vibe} Here&apos;s what attendees said after they
                        used PopIn to find each other in the room.
                    </p>
                </div>
            </section>

            {/* Featured strip */}
            <section className="px-4 sm:px-6 mt-8">
                <div className="max-w-5xl mx-auto">
                    <div
                        data-testid="event-feature-quote"
                        className="relative bg-white border border-neutral-200 scrap-shadow p-10 sm:p-14 rotate-[-0.6deg]"
                    >
                        <MaskingTape
                            color={event.tape}
                            className="-top-4 left-12 w-36 h-8"
                        />
                        <p className="font-editorial italic text-3xl sm:text-4xl text-ink leading-[1.2]">
                            &ldquo;{FEATURED_QUOTES.efficiency.text}&rdquo;
                        </p>
                        <p
                            className="font-handwriting text-xl mt-6"
                            style={{ color: event.color }}
                        >
                            — Attendee, {event.shortName}
                        </p>
                    </div>
                </div>
            </section>

            {/* Reviews grid */}
            <section className="px-4 sm:px-6 mt-20 sm:mt-24">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <h2 className="font-editorial text-4xl sm:text-5xl text-ink leading-tight">
                            What attendees{" "}
                            <span className="italic marker-highlight">
                                actually said
                            </span>
                        </h2>
                        <span className="font-handwriting text-lg text-ink-light">
                            pinned by the community ↓
                        </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                        {rotated.map((t, i) => (
                            <PolaroidCard
                                key={`${event.id}-${t.id}-${i}`}
                                quote={t.text}
                                author={`${t.author} · ${event.shortName}`}
                                tape={t.tape}
                                rotation={t.rotation}
                                delay={i * 70}
                                accent={event.color}
                                testId={`event-testimonial-${event.id}-${i}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Other events */}
            <section className="px-4 sm:px-6 mt-24 sm:mt-32">
                <div className="max-w-7xl mx-auto">
                    <h3 className="font-editorial text-3xl sm:text-4xl text-ink mb-8">
                        Other event reviews
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {EVENTS.filter((e) => e.id !== event.id).map((e) => (
                            <Link
                                key={e.id}
                                to={`/reviews/${e.id}`}
                                data-testid={`related-event-${e.id}`}
                                className="group relative bg-white border border-neutral-200 scrap-shadow p-7 hover:rotate-0 hover:scale-[1.02] transition-all duration-500"
                                style={{ transform: "rotate(-0.5deg)" }}
                            >
                                <MaskingTape
                                    color={e.tape}
                                    className="-top-3 left-10 w-28 h-6"
                                />
                                <p
                                    className="font-marker text-2xl"
                                    style={{ color: e.color }}
                                >
                                    {e.shortName}
                                </p>
                                <p className="font-handwriting text-base text-ink-light mt-2">
                                    {e.city} · {e.host}
                                </p>
                                <span className="font-sans font-bold text-sm text-ink mt-4 inline-block group-hover:text-marker-red">
                                    read →
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="px-4 sm:px-6 mt-24 sm:mt-32">
                <div className="max-w-3xl mx-auto text-center relative">
                    <div className="relative bg-ink text-paper p-12 sm:p-16">
                        <MaskingTape
                            color="yellow"
                            className="-top-4 left-1/2 -translate-x-1/2 w-40 h-8"
                        />
                        <h2 className="font-marker text-4xl sm:text-6xl text-paper leading-[0.95]">
                            Going to {event.shortName}?
                        </h2>
                        <p className="font-editorial italic text-xl text-paper/75 mt-5 max-w-lg mx-auto">
                            Don&apos;t walk in blind. Open PopIn the night
                            before and arrive like you&apos;ve already been
                            there.
                        </p>
                        <div className="mt-8">
                            <PrimaryButton
                                href={BRAND.parentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                testId="event-cta-button"
                                className="!bg-marker-red !border-marker-red !shadow-[6px_6px_0px_0px_rgba(252,235,167,1)] hover:!shadow-[3px_3px_0px_0px_rgba(252,235,167,1)]"
                            >
                                {BRAND.cta}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default EventReviews;
